import { useEffect, useState, useCallback, useRef } from 'react';
import { cardKeyToCard } from '@flip7/shared';
import { Card } from './Card';

import type { ShinyTier } from '@flip7/shared';

interface ShinyUnlockToastProps {
  cardKey: string;
  tier: ShinyTier;
  onComplete: () => void;
}

const TIER_LABELS: Record<ShinyTier, string> = {
  'holo': 'HOLO',
  'reverse-holo': 'REVERSE HOLO',
  'full-art': 'FULL ART',
  'alt-art': 'ALT ART',
  'gold-secret': 'GOLD SECRET',
};

export function ShinyUnlockToast({ cardKey, tier, onComplete }: ShinyUnlockToastProps) {
  const [phase, setPhase] = useState<'enter' | 'showing' | 'exiting'>('enter');
  const completedRef = useRef(false);
  const card = cardKeyToCard(cardKey);

  const fireComplete = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    onComplete();
  }, [onComplete]);

  const dismiss = useCallback(() => {
    if (phase === 'exiting') return;
    setPhase('exiting');
    setTimeout(fireComplete, 300);
  }, [phase, fireComplete]);

  useEffect(() => {
    const enterTimer = setTimeout(() => setPhase('showing'), 50);
    const exitTimer = setTimeout(() => setPhase('exiting'), 3000);
    const doneTimer = setTimeout(fireComplete, 3300);
    return () => {
      clearTimeout(enterTimer);
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
    };
  }, [fireComplete]);

  return (
    <div
      className={`shiny-toast-overlay ${phase === 'enter' ? 'shiny-toast-overlay--enter' : ''} ${phase === 'exiting' ? 'shiny-toast-overlay--exit' : ''}`}
      onClick={dismiss}
    >
      <div className="shiny-toast__burst" />
      <div className="shiny-toast__card">
        <Card card={card} shinyTier={tier} />
      </div>
      <p className="shiny-toast__title">{TIER_LABELS[tier]} UNLOCKED!</p>
    </div>
  );
}
